import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acceptInvite,
  archiveWorkspace,
  createInvite,
  createWorkspace,
  listInvites,
  listMembers,
  listMyMemberships,
  listWorkspaces,
  removeMember,
  revokeInvite,
  saveAccountingExport,
  saveWorkspaceOrder,
  updateMemberRole,
  updateWorkspace,
  type MemberRole,
  type WorkspaceInput,
} from "@/lib/api/workspaces";

export const workspaceKeys = {
  all: ["workspaces"] as const,
  list: () => [...workspaceKeys.all, "list"] as const,
  memberships: (userId: string) => [...workspaceKeys.all, "memberships", userId] as const,
  members: (workspaceId: string) => [...workspaceKeys.all, "members", workspaceId] as const,
  invites: (workspaceId: string) => [...workspaceKeys.all, "invites", workspaceId] as const,
};

export function useWorkspaces(enabled = true) {
  return useQuery({ queryKey: workspaceKeys.list(), queryFn: listWorkspaces, enabled });
}

export function useMyMemberships(userId: string | undefined) {
  return useQuery({
    queryKey: workspaceKeys.memberships(userId ?? ""),
    queryFn: () => listMyMemberships(userId!),
    enabled: Boolean(userId),
  });
}

function useInvalidateWorkspaces() {
  const queryClient = useQueryClient();
  return async () => {
    // A first fetch still in flight was asked before this change and answers without it, and
    // TanStack hands that same request back on invalidation instead of starting a new one - so a
    // person who just accepted an invitation saw no business. Cancel it, then fetch afresh.
    await queryClient.cancelQueries({ queryKey: workspaceKeys.all });
    await queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
  };
}

export function useCreateWorkspace(ownerId: string) {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: (input: WorkspaceInput) => createWorkspace(input, ownerId),
    onSuccess: invalidate,
  });
}

export function useUpdateWorkspace() {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<WorkspaceInput> }) =>
      updateWorkspace(id, input),
    onSuccess: invalidate,
  });
}

export function useArchiveWorkspace() {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({ mutationFn: archiveWorkspace, onSuccess: invalidate });
}

export function useMembers(workspaceId: string | undefined) {
  return useQuery({
    queryKey: workspaceKeys.members(workspaceId ?? ""),
    queryFn: () => listMembers(workspaceId!),
    enabled: Boolean(workspaceId),
  });
}

export function useInvites(workspaceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: workspaceKeys.invites(workspaceId ?? ""),
    queryFn: () => listInvites(workspaceId!),
    enabled: Boolean(workspaceId) && enabled,
  });
}

export function useInviteMember(workspaceId: string, invitedBy: string) {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: Exclude<MemberRole, "owner"> }) =>
      createInvite(workspaceId, email, role, invitedBy),
    onSuccess: invalidate,
  });
}

export function useRevokeInvite() {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({ mutationFn: revokeInvite, onSuccess: invalidate });
}

export function useUpdateMemberRole() {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: MemberRole }) => updateMemberRole(id, role),
    onSuccess: invalidate,
  });
}

export function useRemoveMember() {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({ mutationFn: removeMember, onSuccess: invalidate });
}

/** Export settings and the accounts' Sachkonten; both lists are read again afterwards. */
export function useSaveAccountingExport() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateWorkspaces();
  return useMutation({
    mutationFn: (input: {
      workspaceId: string;
      settings: Record<string, unknown>;
      ledger: Record<string, string>;
    }) => saveAccountingExport(input.workspaceId, input.settings, input.ledger),
    onSuccess: async () => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ["banking"] });
    },
  });
}

export function useAcceptInvite() {
  const invalidate = useInvalidateWorkspaces();
  return useMutation({ mutationFn: acceptInvite, onSuccess: invalidate });
}

/** No refetch needed: the saved user record reaches the auth store, and the list follows it. */
export function useSaveWorkspaceOrder() {
  return useMutation({
    mutationFn: ({ userId, ids }: { userId: string; ids: string[] }) =>
      saveWorkspaceOrder(userId, ids),
  });
}
